import { useEffect, useRef, useState } from "react";
import "@/App.css";
import axios from "axios";
import Lenis from "lenis";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Check, ChevronDown, Instagram, MapPin, Menu, MessageCircle, MoveRight, Phone, Sparkles, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_URL = "https://wa.me/905308692428?text=Merhaba%20Renascor%2C%20bilgi%20almak%20istiyorum.";
const INSTAGRAM_URL = "https://www.instagram.com/renascorofficial?stkn=MXJ0NDAxMG1rOHlkbg==";
const MAPS_URL = "https://maps.app.goo.gl/petwtXRZavAcxRBv5";
const MAP_EMBED = "https://maps.google.com/maps?q=Renascor+Kilo+Kontrol+Merkezi,+%C5%9Eencan+Sk.+No7/A,+35320+Narl%C4%B1dere/%C4%B0zmir&output=embed";
const EASE = [0.22, 1, 0.36, 1];

const images = {
  entrance: "https://customer-assets-m6fa6gv7.emergentagent.net/job_wellness-contact-hub/artifacts/11osk2ha_08f19447-f706-4867-9154-62602555e796.jpeg",
  hero: "https://customer-assets-m6fa6gv7.emergentagent.net/job_wellness-contact-hub/artifacts/tfah1nfr_a88e2af0-7e63-46cb-91b6-0ab8bb4bbcc4.jpeg",
  reformer: "https://customer-assets-m6fa6gv7.emergentagent.net/job_wellness-contact-hub/artifacts/f0mh9cqg_1e345e43-ec2d-42e0-bc3a-68566d012636.jpeg",
  roller: "https://customer-assets-m6fa6gv7.emergentagent.net/job_wellness-contact-hub/artifacts/w5tb6u00_7ecbf54f-6760-48e8-bd2d-af6442da18ae.jpeg",
  vacuum: "https://customer-assets-m6fa6gv7.emergentagent.net/job_wellness-contact-hub/artifacts/w5tb6u00_7ecbf54f-6760-48e8-bd2d-af6442da18ae.jpeg",
  wellness: "https://customer-assets-m6fa6gv7.emergentagent.net/job_wellness-contact-hub/artifacts/11osk2ha_08f19447-f706-4867-9154-62602555e796.jpeg",
};

const heroSlides = [images.hero, images.reformer, images.roller, "/assets/tarti-fast.jpg", images.entrance];

const services = [
  { id: "reformer", number: "01", title: "Reformer Pilates", tag: "Güç & Duruş", image: images.reformer, text: "✨ Kişiye özel egzersiz planı\nMevcut ihtiyaçlara göre bel-boyun fıtığı, skolyoz ve diz problemleri gibi durumlara uygun reformer programı oluşturulur.\n\n🛡️ Hatalı egzersiz riskini önler\nDoğru teknik ve vücut hizası kontrol edilerek hareketlerin güvenli uygulanması desteklenir.\n\n🌿 Ağrı yönetimi ve güven\nZorluk ve direnç seviyesi, kişinin sınırları gözetilerek kontrollü biçimde ayarlanır.\n\n🤝 Tedavi sürecine destek\nDuruş bozukluğu, kas dengesizlikleri ve bel sorunlarında fizyoterapi sürecini destekleyebilir.\n\n👥 Grup veya bireysel ders\nİster motive edici grup dersleriyle ister hedeflerine özel bireysel programla ilerle." },
  { id: "roll-shape", number: "02", title: "Roll Shape", tag: "Sıkılaşma & Bakım", image: images.roller, text: "🔥 Selülit görünümünü azaltır\nCilt altındaki yağ dokusunu hareketlendirerek daha pürüzsüz ve sıkı bir görünüm sağlar.\n\n✨ Lenfatik drenaj etkisi\nRoll cihazı dolaşımı hızlandırarak vücuttaki ödem ve toksinlerin atılmasına destek olur.\n\n🫶 Sıkılaşma ve toparlanma\nVücudun şekillenmesine ve daha pürüzsüz, sıkı bir cilde ulaşılmasına yardımcı olur." },
  { id: "vacu-active", number: "03", title: "Vacu Active", tag: "Bölgesel Destek", image: images.vacuum, text: "🔥 Daha hızlı yağ yakımı\nVakum ve infrared teknolojiyle klasik egzersizlere göre metabolizmayı daha hızlı çalıştırır.\n\n💧 Ödem ve toksinlerden arınma\nArtan dolaşım sayesinde vücudun ödem ve toksinleri atması desteklenir.\n\n✨ Sıkılaşma ve şekillenme\nİncelme ve toparlanma sağlanmaya yardımcı olur." },
  { id: "kilo-analizi", number: "04", title: "Kişiye Özel Kilo Analizi", tag: "Ölçüm & Takip", image: "/assets/tarti-fast.jpg", text: "⚖️ Yağ, kas ve su analizi\nProfesyonel segmental vücut analiz cihazımızla vücut yağ oranını, kas kütleni ve su dengeni hassas şekilde ölçüyoruz.\n\n📊 Metabolik yaş takibi\nMetabolizma hızın ve metabolik yaşın her seansta düzenli olarak izlenir.\n\n🎯 Kişisel yol haritası\nÖlçüm sonuçlarına göre hedeflerin güncellenir, ilerlemen rakamlarla takip edilir." },
];

function WhatsAppButton({ className = "", children = "WhatsApp'tan bilgi al" }) {
  return <a data-testid="whatsapp-cta-button" className={`whatsapp-button ${className}`} href={WHATSAPP_URL} target="_blank" rel="noreferrer"><MessageCircle size={17} />{children}<ArrowUpRight size={16} /></a>;
}

function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div className={className} initial={{ opacity: 0, y: 38 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-70px" }} transition={{ duration: 0.9, delay, ease: EASE }}>
      {children}
    </motion.div>
  );
}

const heroLines = [
  { plain: "İyi hissetmek," },
  { em: "bedende", plain: " başlar." },
];

const Home = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedService, setSelectedService] = useState("Genel bilgi");
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [slide, setSlide] = useState(0);
  const lenisRef = useRef(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-32%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setSlide((s) => (s + 1) % heroSlides.length), 3000);
    return () => clearInterval(timer);
  }, []);

  const scrollTo = (id) => {
    setMenuOpen(false);
    if (lenisRef.current) lenisRef.current.scrollTo(id, { duration: 1.4 });
    else document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const submitContact = async (event) => {
    event.preventDefault();
    setSent(false); setSubmitError(false);
    try {
      await axios.post(`${API}/contact`, { ...form, message: `${selectedService}: ${form.message}` });
      setSent(true); setForm({ name: "", phone: "", message: "" });
    } catch (error) { setSubmitError(true); }
  };

  return (
    <div className="site-shell">
      <header className="nav-wrap">
        <motion.nav className="nav" data-testid="site-navigation" initial={{ y: -70, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.9, delay: 0.25, ease: EASE }}>
          <button className="brand" data-testid="brand-home-button" onClick={() => scrollTo("#anasayfa")}><span className="brand-mark">R</span><span>RENASCOR<small>by elif kardaş</small></span></button>
          <div className={`nav-links ${menuOpen ? "is-open" : ""}`}><button data-testid="nav-services-link" onClick={() => scrollTo("#hizmetler")}>Hizmetler</button><button data-testid="nav-about-link" onClick={() => scrollTo("#hikayemiz")}>Hikayemiz</button><button data-testid="nav-location-link" onClick={() => scrollTo("#konum")}>Konum</button><button data-testid="nav-contact-link" onClick={() => scrollTo("#iletisim")}>İletişim</button></div>
          <WhatsAppButton className="nav-cta" />
          <button className="menu-button" data-testid="mobile-menu-button" onClick={() => setMenuOpen(!menuOpen)}>{menuOpen ? <X /> : <Menu />}</button>
        </motion.nav>
      </header>
      <main>
        <section className="hero" id="anasayfa" ref={heroRef}>
          <motion.div className="hero-slides" style={{ y: bgY }}>
            <AnimatePresence initial={false}>
              <motion.div key={slide} className="hero-slide" style={{ backgroundImage: `url(${heroSlides[slide]})` }} initial={{ opacity: 0, scale: 1.03 }} animate={{ opacity: 1, scale: 1.14 }} exit={{ opacity: 0 }} transition={{ opacity: { duration: 0.9, ease: "easeOut" }, scale: { duration: 3.6, ease: "linear" } }} />
            </AnimatePresence>
          </motion.div>
          <div className="hero-shade" />
          <motion.div className="hero-content" style={{ y: contentY, opacity: contentOpacity }}>
            <motion.div className="eyebrow light" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.35, ease: EASE }}><Sparkles size={15} /> BEDENİNLE YENİDEN BAĞ KUR</motion.div>
            <h1>
              {heroLines.map((line, i) => (
                <span className="hero-line" key={i}>
                  <motion.span initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.45 + i * 0.16, ease: EASE }}>
                    {line.em ? <><em>{line.em}</em>{line.plain}</> : line.plain}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.95, ease: EASE }}>Renascor’da hedefimiz yalnızca değişim değil; kendini daha güçlü, dengeli ve iyi hissettiğin bir yaşam yolculuğu.</motion.p>
            <motion.div className="hero-actions" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.1, ease: EASE }}>
              <WhatsAppButton className="light-button" />
              <button className="text-button" data-testid="hero-services-button" onClick={() => scrollTo("#hizmetler")}>Neler yapıyoruz <MoveRight size={18} /></button>
            </motion.div>
          </motion.div>
          <div className="slide-indicators" data-testid="hero-slide-indicators">
            {heroSlides.map((_, i) => (
              <button key={i} data-testid={`hero-slide-dot-${i}`} className={`slide-dot ${slide === i ? "active" : ""}`} onClick={() => setSlide(i)} aria-label={`Görsel ${i + 1}`} />
            ))}
          </div>
          <motion.div className="hero-note" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.3 }}>
            <strong>0{slide + 1}</strong><span>kişiye özel<br />wellness deneyimi</span>
          </motion.div>
        </section>

        <div className="marquee-band" data-testid="editorial-marquee">
          <div className="marquee-track">
            {[0, 1].map((copy) => (
              <span key={copy} aria-hidden={copy === 1}>Reformer Pilates <i>✦</i> Roll Shape <i>✦</i> Vacu Active <i>✦</i> Kişiye Özel Kilo Analizi <i>✦</i> Kişiye Özel Program <i>✦</i> </span>
            ))}
          </div>
        </div>

        <section className="intro section" id="hikayemiz">
          <Reveal className="intro-label"><span className="vertical-line" /> BİZİM YAKLAŞIMIMIZ</Reveal>
          <Reveal className="intro-copy" delay={0.08}>
            <h2>Değişim bir hedef değil,<br /><em>kendine ayırdığın</em> zamandır.</h2>
            <p>Elif Kardaş’ın özenli yaklaşımıyla Renascor, kilo kontrolünü ve beden farkındalığını bir arada ele alan sıcak bir merkez. Burada her seans, sana özel bir adım.</p>
            <button data-testid="intro-contact-button" className="outline-button" onClick={() => scrollTo("#iletisim")}>Tanışalım <ArrowUpRight size={16} /></button>
          </Reveal>
          <Reveal className="intro-stats" delay={0.16}>
            <div><strong>4</strong><span>özel uygulama<br />alanı</span></div>
            <div><strong>1:1</strong><span>kişiye özel<br />ilgi</span></div>
          </Reveal>
        </section>

        <section className="services section" id="hizmetler">
          <Reveal className="section-heading">
            <div><div className="eyebrow">UYGULAMALARIMIZ</div><h2>Sana iyi gelen<br /><em>ritmi bul.</em></h2></div>
            <p>Her bedenin hikayesi farklı. İhtiyacına uygun uygulamayı birlikte keşfedelim.</p>
          </Reveal>
          <div className="service-grid">
            {services.map((service, index) => (
              <Reveal key={service.id} delay={index * 0.09}>
                <article className="service-card" data-testid={`service-card-${service.id}`}>
                  <div className={`service-image service-image-${service.id}`}><img src={service.image} alt={service.title} /><span>{service.number}</span></div>
                  <div className="service-info">
                    <div className="service-tag">{service.tag}</div>
                    <h3>{service.title}</h3>
                    <p>{service.text}</p>
                    <button data-testid={`service-info-button-${service.id}`} onClick={() => { setSelectedService(service.title); scrollTo("#iletisim"); }}>Fiyat & bilgi al <ArrowUpRight size={16} /></button>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="quote-band">
          <Reveal>
            <div className="quote-mark">“</div>
            <blockquote>Her gün kendin için<br /><em>küçük bir adım.</em></blockquote>
            <div className="quote-caption">RENASCOR / ELİF KARDAŞ</div>
          </Reveal>
        </section>

        <section className="contact section" id="iletisim">
          <Reveal className="contact-intro">
            <div className="eyebrow">BİRLİKTE BAŞLAYALIM</div>
            <h2>Merak ettiğin<br /><em>ne varsa sor.</em></h2>
            <p>Uygulamalarımız, paketlerimiz veya sana uygun yol haritası için bize ulaş. Fiyat bilgisi ve randevu için WhatsApp’tan yazabilir ya da formu doldurabilirsin.</p>
            <div className="socials">
              <a data-testid="instagram-link" href={INSTAGRAM_URL} target="_blank" rel="noreferrer"><Instagram size={18} /> Instagram</a>
              <a data-testid="facebook-link" href="https://facebook.com" target="_blank" rel="noreferrer"><span className="facebook-icon">f</span> Facebook</a>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <form className="contact-form" data-testid="contact-form" onSubmit={submitContact}>
              <label>Adın soyadın<input data-testid="contact-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nasıl hitap edelim?" /></label>
              <label>Telefon numaran<input data-testid="contact-phone-input" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="05__ ___ __ __" /></label>
              <label>İlgilendiğin uygulama<select data-testid="contact-service-select" value={selectedService} onChange={(e) => setSelectedService(e.target.value)}>{["Genel bilgi", ...services.map((s) => s.title)].map((name) => <option key={name}>{name}</option>)}</select></label>
              <label>Mesajın<textarea data-testid="contact-message-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Sana nasıl yardımcı olabiliriz?" rows="3" /></label>
              {sent && <div className="success-message" data-testid="contact-success-message"><Check size={17} /> Talebin bize ulaştı, teşekkür ederiz.</div>}
              {submitError && <div className="error-message" data-testid="contact-error-message">Mesaj gönderilemedi. Lütfen tekrar deneyin veya WhatsApp’tan bize yazın.</div>}
              <button data-testid="contact-submit-button" className="submit-button" type="submit">Gönder <MoveRight size={18} /></button>
            </form>
          </Reveal>
        </section>

        <section className="location section" id="konum">
          <Reveal className="location-info">
            <div className="eyebrow">BİZE UĞRA</div>
            <h2>Narlıdere’de,<br /><em>sana yakınız.</em></h2>
            <div className="location-rows">
              <div className="location-row" data-testid="location-address"><MapPin size={19} /><div><strong>Adres</strong>Şencan Sk. No:7/A, Narlı<br />35320 Narlıdere / İzmir</div></div>
              <div className="location-row" data-testid="location-phone"><Phone size={19} /><div><strong>Telefon & WhatsApp</strong>0530 869 24 28</div></div>
              <div className="location-row" data-testid="location-instagram"><Instagram size={19} /><div><strong>Instagram</strong>@renascorofficial</div></div>
            </div>
            <a data-testid="directions-button" className="outline-button" href={MAPS_URL} target="_blank" rel="noreferrer">Yol tarifi al <ArrowUpRight size={16} /></a>
          </Reveal>
          <Reveal className="location-map" delay={0.12}>
            <iframe data-testid="location-map-iframe" src={MAP_EMBED} title="Renascor Kilo Kontrol Merkezi konumu" loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
          </Reveal>
        </section>

        <section className="faq section">
          <Reveal>
            <div className="eyebrow">MERAK EDİLENLER</div>
            <h2>İlk adımın<br /><em>cevapları.</em></h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="faq-list">
              {[{ q: "İlk görüşmede ne oluyor?", a: "Öncelikle seni ve hedeflerini dinliyoruz. Vücut analiz ölçümünle bedenini tanıyıp sana uygun uygulama ve seans önerisini birlikte oluşturuyoruz." }, { q: "Fiyat bilgisi nasıl alabilirim?", a: <>Paket ve seans seçenekleri kişiye göre değişebildiği için sana özel fiyat bilgisini <a href={WHATSAPP_URL} target="_blank" rel="noreferrer">WhatsApp</a> veya <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer">Instagram</a> adresimiz üzerinden bize ulaşarak alabilirsin.</> }, { q: "Uygulamalara kimler katılabilir?", a: "Her uygulamanın farklı ihtiyaçlara uygunluğu var. Kısa bir ön görüşmeyle senin için en doğru seçeneği belirliyoruz." }].map((item, index) => (
                <div className={`faq-item ${openFaq === index ? "open" : ""}`} key={item.q}>
                  <button data-testid={`faq-button-${index}`} onClick={() => setOpenFaq(openFaq === index ? -1 : index)}><span>0{index + 1}</span>{item.q}<ChevronDown size={19} /></button>
                  {openFaq === index && <p data-testid={`faq-answer-${index}`}>{item.a}</p>}
                </div>
              ))}
            </div>
          </Reveal>
        </section>
      </main>
      <footer className="footer">
        <div className="brand footer-brand"><span className="brand-mark">R</span><span>RENASCOR<small>by elif kardaş</small></span></div>
        <span>© 2026 Renascor Kilo Kontrol Merkezi — Narlıdere / İzmir</span>
        <span>İyi hissetmek, bedende başlar.</span>
      </footer>
      <a data-testid="floating-whatsapp-button" className="floating-wa" href={WHATSAPP_URL} target="_blank" rel="noreferrer"><MessageCircle size={22} /><span>WhatsApp</span></a>
    </div>
  );
};

function App() {
  return <Home />;
}

export default App;
